import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Send, CheckCircle2, HandCoins, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { StatusBadge } from "../shared/StatusBadge";
import { GstBreakdown } from "./GstBreakdown";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { paymentStateMeta } from "../../lib/statusMaps";
import { formatCurrency, formatDate, formatEpoch } from "../../lib/utils";
import { useTaxConfig, computeGst } from "../../lib/tax";

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// Unified payment actions for a subscription. Updates its own local copy of the
// payment so saving a discount / copying / sending a link does NOT refresh or
// close the window; the parent is synced once, when the dialog closes.
export function PaymentDialog({ open, onOpenChange, payment, onDone }) {
  const cfg = useTaxConfig();
  const [pay, setPay] = useState(payment);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false); // did anything change while open?
  const [amountStr, setAmountStr] = useState("");
  const [discountStr, setDiscountStr] = useState("");
  const [discountType, setDiscountType] = useState("amount");
  const [method, setMethod] = useState("cash");
  const [txn, setTxn] = useState("");
  const [newState, setNewState] = useState("");

  // Sync local copy + reset the editor whenever a (different) payment opens.
  useEffect(() => {
    if (!payment) return;
    setPay(payment);
    const a = Number(payment.amout || 0);
    const rb = Number(payment.original_amount || payment.amout || 0);
    const d = round2(rb - a);
    setAmountStr(a ? String(a) : "");
    setDiscountType("amount");
    setDiscountStr(d > 0 ? String(d) : "");
    setMethod("cash");
    setTxn("");
    setNewState("");
    setDirty(false);
    // Intentionally keyed to a (different) payment opening — the parent passes a
    // fresh `payment` object each render, so including it would reset the editor
    // (and lose in-progress edits) on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment?.id, open]);

  if (!pay) return null;
  const meta = paymentStateMeta(pay.payment_state);
  const pid = pay.id;
  const link = pay.payment_link;
  const isPaid = pay.payment_state === "paid";

  const refBase = Number(pay.original_amount || pay.amout || 0);
  const base = amountStr === "" ? 0 : Math.max(Number(amountStr) || 0, 0);
  const bd = computeGst(base, cfg, refBase);
  const discValue = round2(refBase - base);
  const discPct = refBase > 0 ? (discValue / refBase) * 100 : 0;
  const changed = round2(base) !== round2(Number(pay.amout || 0));

  const onAmount = (v) => {
    setAmountStr(v);
    const b = v === "" ? 0 : Math.max(Number(v) || 0, 0);
    const dAmt = round2(refBase - b);
    setDiscountStr(dAmt > 0 ? String(discountType === "percent" ? round2(refBase > 0 ? (dAmt / refBase) * 100 : 0) : dAmt) : "");
  };
  const onDiscount = (v) => {
    setDiscountStr(v);
    const dv = v === "" ? 0 : Number(v) || 0;
    const dAmt = discountType === "percent" ? (refBase * dv) / 100 : dv;
    setAmountStr(String(Math.max(round2(refBase - dAmt), 0)));
  };
  const onToggleType = (t) => {
    setDiscountType(t);
    const dAmt = round2(refBase - base);
    setDiscountStr(dAmt > 0 ? String(t === "percent" ? round2(refBase > 0 ? (dAmt / refBase) * 100 : 0) : dAmt) : "");
  };
  const resetStandard = () => { setAmountStr(String(refBase || "")); setDiscountStr(""); };

  // Close handler — sync the parent once (so the page reflects any change).
  const handleClose = (v) => {
    if (!v && dirty) onDone?.();
    onOpenChange(v);
  };

  const saveAmount = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/payments/${pid}/set-amount`, { amount: base });
      setPay((p) => ({ ...p, amout: data.amout, original_amount: data.original_amount,
        discount_type: data.discount_type, discount_value: data.discount_value,
        payment_link: data.payment_link || p.payment_link }));
      setDirty(true);
      toast.success("Amount updated.");
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not update amount."));
    } finally { setBusy(false); }
  };

  const sendLink = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/payments/${pid}/link`, { send: true });
      setPay((p) => ({ ...p, payment_link: data.payment_link || p.payment_link }));
      setDirty(true);
      toast.success("Payment link sent.");
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not send payment link."));
    } finally { setBusy(false); }
  };

  const copyLink = async () => {
    setBusy(true);
    try {
      let url = link?.url;
      if (!url) {
        const { data } = await apiClient.post(`/payments/${pid}/link`, { send: false });
        setPay((p) => ({ ...p, payment_link: data.payment_link || p.payment_link }));
        setDirty(true);
        url = data?.payment_link?.url;
      }
      if (!url) { toast.error("No payment link available."); return; }
      await navigator.clipboard.writeText(url);
      toast.success("Payment link copied to clipboard");
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not copy payment link."));
    } finally { setBusy(false); }
  };

  const markPaid = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/payments/${pid}/mark-paid`, { payment_method: method, transaction_no: txn || null });
      setDirty(true);
      toast.success("Marked as paid.");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not mark as paid."));
    } finally { setBusy(false); }
  };

  const applyState = async () => {
    if (!newState || newState === pay.payment_state) return;
    setBusy(true);
    try {
      await apiClient.post(`/payments/${pid}/set-state`, { state: newState });
      setDirty(true);
      toast.success("Payment status updated.");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not update payment status."));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl" data-testid="payment-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <DialogTitle>Manage Payment</DialogTitle>
              <DialogDescription>Set the pre-tax amount to receive (GST is added on top), record payment, or share the link.</DialogDescription>
            </div>
            <div className="flex flex-col items-end gap-1">
              <StatusBadge label={meta.label} variant={meta.variant} data-testid="payment-dialog-state" />
              {pay.approval_source && (
                <span
                  className={`text-xs font-medium ${pay.approval_source === "stripe" ? "text-info" : "text-text-muted"}`}
                  data-testid="payment-dialog-approval-source"
                >
                  {pay.approval_source === "stripe" ? "Stripe confirmed" : "Manually approved"}
                </span>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="grid gap-5 md:grid-cols-2">
          {/* LEFT — live amount summary + link */}
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-background p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">Amount to Receive</p>
              <GstBreakdown bd={bd} testId="payment-dialog-breakdown" />
            </div>

            {pay.payment_state === "collected" && (
              <div className="rounded-md border border-info/40 bg-info-bg p-3 text-sm text-info" data-testid="payment-dialog-collected-note">
                <HandCoins size={15} className="mr-1 inline" strokeWidth={1.5} />
                Driver collected {formatCurrency(pay.collected_amount || pay.amout)}
                {pay.collected_at ? ` on ${formatDate(pay.collected_at)}` : ""}. Confirm you received it, then mark paid.
              </div>
            )}

            {link && (
              <div className="rounded-md border border-border bg-background p-3" data-testid="payment-dialog-link-block">
                <div className="mb-1.5 flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Payment Link</p>
                  {link.send_count > 0 ? (
                    <span className="rounded-full bg-success-bg px-2 py-0.5 text-xs font-medium text-success" data-testid="payment-dialog-send-count">
                      Sent {link.send_count}×
                    </span>
                  ) : (
                    <span className="rounded-full bg-surface px-2 py-0.5 text-xs text-text-muted" data-testid="payment-dialog-send-count">
                      Not sent yet
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 rounded border border-border bg-surface px-2 py-1.5">
                  <span className="flex-1 truncate text-xs text-text-muted" data-testid="payment-dialog-link-url">{link.url}</span>
                </div>
                {link.send_history?.length > 0 && (
                  <div className="mt-2 space-y-0.5" data-testid="payment-dialog-send-history">
                    {link.send_history.slice(-5).reverse().map((t, i) => (
                      <p key={i} className="text-xs text-text-muted">Sent {formatEpoch(t)}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT — actions */}
          <div className="space-y-5">
            {isPaid ? (
              <div className="rounded-md border border-success/40 bg-success-bg p-3 text-sm text-success" data-testid="payment-dialog-paid-note">
                <CheckCircle2 size={15} className="mr-1 inline" strokeWidth={1.5} />
                This payment is marked Paid.
              </div>
            ) : (
              <>
                <div className="space-y-3 rounded-lg border border-border p-3">
                  <div className="flex items-center justify-between">
                    <Label>Amount to receive (before GST)</Label>
                    {refBase > 0 && (
                      <button type="button" className="flex items-center gap-1 text-xs text-text-muted underline" onClick={resetStandard} data-testid="payment-dialog-reset-standard">
                        <RotateCcw size={12} /> Standard {formatCurrency(refBase)}
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">$</span>
                    <Input type="number" min="0" step="0.01" value={amountStr} onChange={(e) => onAmount(e.target.value)}
                      className="pl-6 font-heading text-lg" placeholder="0.00" data-testid="payment-dialog-amount-input" />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs text-text-muted">Or apply a discount off {formatCurrency(refBase)}</Label>
                    <div className="flex gap-2">
                      <select value={discountType} onChange={(e) => onToggleType(e.target.value)}
                        className="rounded-md border border-border bg-surface px-2 text-sm" data-testid="payment-dialog-discount-type">
                        <option value="amount">Amount ($)</option>
                        <option value="percent">Percent (%)</option>
                      </select>
                      <Input type="number" min="0" value={discountStr} onChange={(e) => onDiscount(e.target.value)}
                        placeholder={discountType === "percent" ? "e.g. 10" : "e.g. 5.00"} data-testid="payment-dialog-discount-value" />
                    </div>
                    <p className="text-xs font-medium text-accent-hover" data-testid="payment-dialog-discount-preview">
                      {discValue > 0.001
                        ? `Discount ${formatCurrency(discValue)} · ${discPct.toFixed(1)}%`
                        : discValue < -0.001
                          ? `Extra ${formatCurrency(Math.abs(discValue))} above standard`
                          : "No discount"}
                    </p>
                  </div>

                  <Button size="sm" disabled={busy || !changed} onClick={saveAmount} data-testid="payment-dialog-save-amount">
                    Save amount ({formatCurrency(bd.total)} incl. GST)
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label>Record payment (cash / Interac / etc.)</Label>
                  <div className="flex gap-2">
                    <select value={method} onChange={(e) => setMethod(e.target.value)}
                      className="rounded-md border border-border bg-surface px-2 text-sm" data-testid="payment-dialog-method">
                      <option value="cash">Cash</option>
                      <option value="interac">Interac</option>
                      <option value="online">Online / Other</option>
                    </select>
                    <Input value={txn} onChange={(e) => setTxn(e.target.value)} placeholder="Reference no. (optional)" data-testid="payment-dialog-txn" />
                  </div>
                  <Button variant="accent" size="sm" disabled={busy} onClick={markPaid} data-testid="payment-dialog-mark-paid">
                    <CheckCircle2 size={14} strokeWidth={1.5} /> Mark Paid
                  </Button>
                </div>

                <div className="space-y-2 border-t border-border pt-3">
                  <Label>Payment link</Label>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" disabled={busy} onClick={sendLink} data-testid="payment-dialog-send-link">
                      <Send size={14} strokeWidth={1.5} /> {link?.send_count > 0 ? "Resend Payment Link" : "Send Payment Link"}
                    </Button>
                    <Button variant="outline" size="sm" disabled={busy} onClick={copyLink} data-testid="payment-dialog-copy-link">
                      <Copy size={14} strokeWidth={1.5} /> Copy Payment Link
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="space-y-2 rounded-lg border border-border bg-background p-4" data-testid="payment-dialog-set-state">
          <Label>Set status manually</Label>
          <p className="text-xs text-text-muted">
            Overrides this payment's status without charging a card or creating a link. A manual change is never treated as a Stripe-confirmed payment.
          </p>
          <div className="flex flex-wrap gap-2">
            <select
              value={newState}
              onChange={(e) => setNewState(e.target.value)}
              className="rounded-md border border-border bg-surface px-2 py-2 text-sm"
              data-testid="payment-dialog-state-select"
            >
              <option value="">Change status…</option>
              <option value="unpaid">Unpaid / Pending</option>
              <option value="cash_pending">Cash Collection Pending</option>
              <option value="collected">Cash Collected</option>
              <option value="paid">Paid / Received</option>
              <option value="awaiting">Awaiting Online Payment</option>
              <option value="declined">Declined / Abandoned</option>
            </select>
            <Button
              variant="accent" size="sm"
              disabled={busy || !newState || newState === pay.payment_state}
              onClick={applyState}
              data-testid="payment-dialog-apply-state"
            >
              Apply status
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
